(ns ycweb.app.website.common
  (:require
   [ajax.core :as ajax]
   [rum.core :as rum]
   [util.string :as str]
   [ycweb.components.common :as cmn]
   [ycweb.components.grid :as grid]
   [ycweb.components.scroll :as scroll]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;; Make sure the :class in img-props is a vector, not a string.
(rum/defc lazy-image < rum/reactive [app-atom public_id img-props {:keys [w mw h max-dpr alt] :as cloudinary-config}]
  ;; Note: This leverages the lazysizes javascript.
  ;; It would be _possible_ to rework this. But it's clean vanilla JS that interops correctly, so there's not much point.

  (let [full-url
        (u/cloudinary public_id
          (u/maybe assoc cloudinary-config))

        ;; This value is updated when the browser size updates (with 200ms debounce)
        screen (cmn/curact app-atom [:device :screen])

        ;; for SSR, we have access to [:device :mobile?], which is an probabilistic check based on user-agent
        mobile? (cmn/curact app-atom [:device :mobile?])

        ;; Use mw (mobile width) if screen < 759 and we have a screen width at all.
        w
        (cond
          (not mw) w
          mobile? mw

          (and
            (pos? (or (:width screen) 0))
            (< (:width screen) 759))
          mw

          :else
          w)

        lowres-url
        (u/cloudinary public_id
          (u/maybe assoc cloudinary-config
            :w (if w (/ (u/parseInt w) 10) "full")
            :h (if h (/ (u/parseInt h) 10) "full")
            :q 10))]
    [:picture
     {:key public_id}
     [:source
      {:data-srcset
       (->>
         (for [d (range (or max-dpr 1))]
           (str
             (u/cloudinary public_id
               (u/maybe assoc cloudinary-config :dpr (u/number-format (inc d) 0)))
             " " (inc d) "x"))
         (interpose ", ")
         (apply str))}]
     [:img
      (merge
        img-props
        (u/maybe assoc
          {:src lowres-url
           :data-src full-url
           :class
           (vec
             (conj
               (if (string? (:class img-props))
                 [(:class img-props)] (:class img-props))
               "lazyload"))
           :alt alt}
          :width w
          :height h))]]))

(defn- parse-cloudinary-specs
  "Parses component props into a Cloudinary transformation spec map for a single transformation.
   Handles :original, :quality, :fetch_format, :crop, :effect, and other common Cloudinary params."
  [props]
  (let [{:keys [original width height effect crop quality fetch_format dpr border gravity] :as p} props]
    (if original
      ;; For original, only pass through specified Cloudinary transform keys.
      ;; No other defaults are applied from this function.
      (select-keys p [:width :height :crop :quality :fetch_format :dpr :original])
      (as-> (merge {:quality "auto" :fetch_format "auto" :crop "lpad"} p) specs
        (if (and width (<= width 300) (not effect))
          (assoc specs :effect "sharpen:40")
          specs)
        ;; Add other transformations like border, gravity if they are present in p
        (cond-> specs
          border (assoc :border border)
          gravity (assoc :gravity gravity))
        ;; Select only known Cloudinary transformation keys to pass to the URL builder.
        ;; :original is not a transform key itself but controls behavior here.
        (select-keys specs [:width :height :crop :quality :fetch_format :effect :dpr :border :gravity])))))

(rum/defc cloudinary-lazy-image < rum/reactive
  "Renders a Cloudinary image using lazy-image. This function signature is compatible with the PHP equivalent.
   Props:
   - :public_id (String, required)
   - :alt (String)
   - :html-class (String) - CSS class for the <img> tag
   - :id (String) - ID for the <img> tag
   - :width_attr (Int) - HTML width attribute for <img>
   - :height_attr (Int) - HTML height attribute for <img>
   - :lazyload (Boolean) - Ignored, as lazy-image always uses lazy loading
   - :original (Boolean) - Defaults to false. If true, requests original image with minimal transforms.
   - :max_dpr (Int) - Maximum DPR to generate sources for (e.g., 2 or 3). Defaults to 1.
   - Other Cloudinary transformation params like :width, :height, :crop, :quality, etc."
  [app-atom {:keys [public_id alt html-class id width_attr height_attr lazyload original max_dpr] :as props}]
  (when (seq public_id)
    (let [base-specs (parse-cloudinary-specs props)

          ;; Map HTML attributes for img-props (lazy-image format)
          img-props
          (cond-> {}
            alt (assoc :alt alt)
            html-class (assoc :class html-class)
            id (assoc :id id)
            width_attr (assoc :width width_attr)
            height_attr (assoc :height height_attr))

          ;; Map cloudinary config (transform :width/:height to :w/:h for lazy-image)
          cloudinary-config

          (cond-> {}
            (:width base-specs) (assoc :w (:width base-specs))
            (:height base-specs) (assoc :h (:height base-specs))
            (:crop base-specs) (assoc :crop (:crop base-specs))
            (:quality base-specs) (assoc :quality (:quality base-specs))
            (:fetch_format base-specs) (assoc :fetch_format (:fetch_format base-specs))
            (:effect base-specs) (assoc :effect (:effect base-specs))
            (:dpr base-specs) (assoc :dpr (:dpr base-specs))
            (:border base-specs) (assoc :border (:border base-specs))
            (:gravity base-specs) (assoc :gravity (:gravity base-specs))
            max_dpr (assoc :max-dpr max_dpr)
            alt (assoc :alt alt))]

      (lazy-image app-atom public_id img-props cloudinary-config))))

(defn add-to-cart
  [app-atom {:keys [url success-callback-fn error-fn params confirm-key error-key]}]
  #?(:cljs
     (let [data (js/FormData.)]
       (toaster/open-toast app-atom :processing "Adding item to cart" {})
       (doseq [[k v] params]
         (.append data (name k) v))
       ; Once add to cart is live through a CLJ api, use that instead.
       (ajax/POST url
         {:body data
          :response-format :json
          :keywords? true
          :handler
          (fn [res]
            (do
              (toaster/close-toast app-atom)
              (cond
                (and (:success res) success-callback-fn)
                (success-callback-fn res)

                (:success res)
                (do
                  (grid/dispatch-action app-atom
                    (or confirm-key :web.common/cart-mini-qty) {})
                  (grid/confirm-modal app-atom
                    {:title "This item has been added to your cart."
                     :confirm-key :web.common/show-cart-mini-qty
                     :confirm-text "View Cart"
                     :cancel-text "Continue Shopping"}))

                :else
                (grid/open-modal app-atom
                  (or error-key :web.common/add-to-cart)
                  {:config
                   {:title "Something went wrong"}}))))
          :error-handler
          (do
            (toaster/close-toast app-atom)
            (if error-fn
              (error-fn)
              (fn [res]
                (grid/open-modal app-atom :web.common/add-to-cart
                  {:config
                   {:title "Something went wrong"}}))))
          :timeout 60000}))))

(rum/defc add-to-cart-error []
  [:div.yc-popup.yc-popup--has-footer
   [:div.yc-popup__content
    [:p "We didn't manage to add this item to your cart. Please try again, or "
     [:a {:href "contact.htm"} "contact us"]
     " for help."]]])

(defmethod grid/modal-config :web.common/add-to-cart [_ _ _]
  {:body-raw add-to-cart-error
   :modal-type :alert})

(defmethod grid/action :web.common/cart-mini-qty [app-atom config _]
  #?(:cljs
     (do
       (ajax/GET "/cart.htm?action=miniqty"
         {:response-format :json
          :keywords? true
          :handler
          (fn [response]
            (js/addToCartUpdate response))})
       (js/updateHeaderCartPopup))))

(rum/defc pagination-control < rum/reactive
  [app-atom page-atom pagecount
   {:keys [scroll-to navigate grid-action loading-overlay show-summary? result-count action] :as options}]
  (let [{:keys [page pagesize showpages] :or {showpages 4}} (rum/react page-atom)
        page (or page 1)
        startpage
        (cond
          (> page (- pagecount showpages))
          (max (- pagecount showpages) 1)

          (> page showpages)
          (- page showpages)

          :else
          1)
        endpage
        (min (+ page showpages) pagecount)

        app-page (cmn/curact app-atom [:page])
        {:keys [params base]} ((:screen app-page) app-page)
        cleaned-params (urlstate/clean-params params)

        default-on-click-fn
        (fn [e p-num]
          (swap! page-atom assoc :page p-num)
          (when (u/presence scroll-to) (scroll/scroll-to-id e scroll-to)))

        link-on-click-fn
        (fn [e p-num]
          #?(:cljs
             (.preventDefault e))
          (when (u/presence loading-overlay)
            (toaster/open-overlay app-atom loading-overlay))
          (when (u/presence scroll-to) (scroll/scroll-to-id e scroll-to))
          (grid/dispatch-action app-atom grid-action {:page p-num}))]

    (when (> pagecount 1)
      [:nav.pagination-wrapper
       [:ul.pagination.pagination--centered

        (when (> page 1)
          [:li.pagination__item
           [:a
            (cond
              (and (u/presence grid-action) (= navigate :url))
              (merge
                (urlstate/href-props app-atom base action (assoc cleaned-params :page (max (dec page) 1)))
                {:on-click #(link-on-click-fn % (max (dec page) 1))})

              (= navigate :url)
              (urlstate/href-props app-atom base action (assoc cleaned-params :page (max (dec page) 1)))

              :else
              {:href "#"
               :on-click #(default-on-click-fn % (max (dec page) 1))})
            "Prev"]])

        (when (> startpage 1)
          [:li.pagination__item.pagination__item--ellipsis [:span "..."]])

        (map
          (fn [pagenumber]
            [:li
             {:key pagenumber :class (str "pagination__item" (when (= page pagenumber) " pagination__item--active"))}
             [:a
              (cond
                (and (u/presence grid-action) (= navigate :url))
                (merge
                  (urlstate/href-props app-atom base action (assoc cleaned-params :page pagenumber))
                  {:on-click #(link-on-click-fn % pagenumber)})

                (= navigate :url)
                (urlstate/href-props app-atom base action (assoc cleaned-params :page pagenumber))

                :else
                {:href "#"
                 :on-click #(default-on-click-fn % pagenumber)})
              pagenumber]])
          (range startpage (if (= pagecount endpage) (+ endpage 1) endpage)))

        (when (< endpage pagecount)
          [:li.pagination__item.pagination__item--ellipsis [:span "..."]])

        (when (< page pagecount)
          [:li.pagination__item
           [:a
            (cond
              (and (u/presence grid-action) (= navigate :url))
              (merge
                (urlstate/href-props app-atom base action (assoc cleaned-params :page (u/number-format (min (inc page) pagecount))))
                {:on-click #(link-on-click-fn % (min (inc page) pagecount))})

              (= navigate :url)
              (urlstate/href-props app-atom base action (assoc cleaned-params :page (u/number-format (min (inc page) pagecount))))

              :else
              {:href "#"
               :on-click #(default-on-click-fn % (min (inc page) pagecount))})
            "Next"]])]

       (when (and show-summary? (> pagecount 1))
         [:p.yc-paragraph.yc-paragraph--small.u-text-center.u-margin-top-20
          "Page " page " of " (u/number-format pagecount) (if (u/presence result-count) (str " from " result-count " results.") ".")

          (when
            (and (> pagecount 2) (not (= page 1)))
            (list
              [:span.u-pipe {:key "pagination-first-page-pipe"} "|"]
              [:a
               (cond
                 (and (u/presence grid-action) (= navigate :url))
                 (merge
                   (urlstate/href-props app-atom base action (assoc cleaned-params :page 1))
                   {:key "pagination-first-page-link"
                    :class (u/bem :text-link [:muted])
                    :on-click #(link-on-click-fn % 1)})

                 (= navigate :url)
                 (merge
                   (urlstate/href-props app-atom base action (assoc cleaned-params :page 1))
                   {:key "pagination-first-page-link"
                    :class (u/bem :text-link [:muted])})

                 :else
                 {:key "pagination-first-page-link"
                  :href "#"
                  :class (u/bem :text-link [:muted])
                  :on-click #(default-on-click-fn % 1)})
               "First page"]))

          (when (and (> pagecount 2) (not (= page pagecount)))
            (list
              [:span.u-pipe {:key "pagination-last-page-pipe"} "|"]
              [:a
               (cond
                 (and (u/presence grid-action) (= navigate :url))
                 (merge
                   (urlstate/href-props app-atom base action (assoc cleaned-params :page (u/number-format pagecount)))
                   {:key "pagination-last-page-link"
                    :class (u/bem :text-link [:muted])
                    :on-click #(link-on-click-fn % pagecount)})

                 (= navigate :url)
                 (merge
                   (urlstate/href-props app-atom base action (assoc cleaned-params :page (u/number-format pagecount)))
                   {:key "pagination-last-page-link"
                    :class (u/bem :text-link [:muted])})

                 :else
                 {:key "pagination-last-page-link"
                  :href "#"
                  :class (u/bem :text-link [:muted])
                  :on-click #(default-on-click-fn % pagecount)})
               "Last page"]))])])))

(rum/defc user-avatar < rum/reactive
  [{:user/keys [avatar-letter avatar-colour public-id] :as user}
   & [{:keys [width height] :as options}]]
  (cond
    (u/presence public-id)
    [:img.avatar
     {:src (u/cloudinary public-id {:q "auto" :w (if width width 64) :h (if height height 64) :c "pad"})
      :class (u/bem :yc-simple-image [:full])
      :width (if width width 64)
      :height (if height height 64)}]

    :else
    [:div {:class (str "missing-avatar" (or (when avatar-colour (str " missing-avatar--" avatar-colour)) " u-bg-oil"))}
     [:div.missing-avatar__text
      (or
        avatar-letter
        [:i.icon.icon--user.icon--is-light.icon--medium-large.icon--block])]]))

(defn read-more [content & [length]]
  (let [length (or length 400)]
    (if (< (count content) length)
      content
      (let [show (clojure.core/subs content 0 length)
            hide (clojure.core/subs content length)]
        [:span
         show
         [:span.js-collapsible-text__content {:style {:display "none"}} hide]
         " "
         [:a.text-link.collapsible-text__link.js-collapsible-text__link {:href "#"} "Read more"]]))))

(defn set-meta-title!
  ([title] (set-meta-title! title title))
  ([title og-title]
   #?(:cljs
      (do
        (set! (. js/document -title) title)
        (when-let [og-tag (js/document.querySelector "meta[property='og:title']")]
          (.setAttribute og-tag "content" og-title))))))
